'use client';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { useTemplates } from '../hooks/use-templates';
import type { ChecklistTemplate } from '../types/checklist.types';

interface ChecklistTemplatesProps {
  onSelectTemplate: (template: ChecklistTemplate) => void;
  onCreateNew: () => void;
  onDeleteTemplate?: (template: ChecklistTemplate) => void;
}

import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { checklistService } from '../services/checklist-service';
function CreateChecklistButton({ template }: { template: ChecklistTemplate }) {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateChecklist = async (template: ChecklistTemplate) => {
    setLoading(true);
    setError(null);
    try {
      if (!activeTenancy?.id || !user?.uid) {
        throw new Error('Missing required user or tenancy information');
      }
      const checklist = await checklistService.createChecklistFromTemplate(template.id, activeTenancy.id, user.uid);
      // Navigate to checklists page and open edit dialog for the new checklist
      navigate(`/checklists`, {
        state: {
          editChecklistId: checklist.id,
          activeTab: 'custom'
        }
      });
    } catch (err: any) {
      console.error('Create Checklist error:', err);
      setError(err?.message ? `Failed to create checklist: ${err.message}` : 'Failed to create checklist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className="w-full cursor-pointer bg-primary hover:bg-primary/30"
        onClick={(e) => {
          e.stopPropagation();
          handleCreateChecklist(template);
        }}
        disabled={loading}
        tabIndex={-1}
        type="button"
      >
        {loading ? 'Creating...' : 'Use Checklist'}
      </Button>
      {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
    </>
  );
}

export function ChecklistTemplates({ onSelectTemplate, onCreateNew, onDeleteTemplate }: ChecklistTemplatesProps) {
  const { templates, loading, error } = useTemplates();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-red-500">
          <p>Error loading templates: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Create New Template Button */}
      <div 
        className="group relative overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/20 hover:border-primary/40 transition-all duration-200 cursor-pointer bg-muted/10 hover:bg-muted/20"
        onClick={onCreateNew}
      >
        <div className="flex items-center justify-center py-8 px-6">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
              <Plus className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mt-3 text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
              Create New Template
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Start from scratch with a blank template
            </p>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className="space-y-3">
        {templates.map((template) => (
          <div
            key={template.id}
            className="group relative overflow-hidden rounded-lg border border-border hover:border-primary/20 bg-card hover:bg-accent/5 transition-all duration-200"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                      {template.title}
                    </h3>
                    {/* Ownership Badge */}
                    <Badge 
                      variant={template.tenantId === 'system' || template.createdBy === 'system' ? 'secondary' : 'default'}
                      className={`text-xs ${template.tenantId === 'system' || template.createdBy === 'system' 
                        ? 'bg-blue-100 text-blue-800 border-blue-200' 
                        : 'bg-green-100 text-green-800 border-green-200'
                      }`}
                    >
                      {template.tenantId === 'system' || template.createdBy === 'system' ? 'System' : 'Custom'}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                    {template.description}
                  </p>
                </div>
                <Badge 
                  variant="outline" 
                  className={`ml-4 flex-shrink-0 ${template.badgeClass} group-hover:border-primary/30`}
                >
                  {template.standard}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span className="flex items-center">
                    <span className="font-medium text-foreground">{template.items?.length || 0}</span>
                    <span className="ml-1">items</span>
                  </span>
                  <span>•</span>
                  <span>Updated {template.updatedAt.toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onSelectTemplate(template); 
                    }} 
                    disabled={loading} 
                    type="button"
                  >
                    Edit Template
                  </Button>
                  {onDeleteTemplate && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to delete the template "${template.title}"? This action cannot be undone.`)) {
                          onDeleteTemplate(template);
                        }
                      }}
                      disabled={loading}
                      type="button"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <CreateChecklistButton template={template} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hover Effect */}
            <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/10 rounded-lg transition-all duration-200 pointer-events-none" />
          </div>
        ))}
      </div>
      
      {templates.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            <p className="text-lg">No templates found</p>
            <p className="text-sm mt-1">Create your first template to get started</p>
          </div>
        </div>
      )}
    </div>
  );
}
